import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail as Mail, FiPhone as Phone, FiLock as Lock, FiCheckCircle as CheckCircle } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import Logo from '../common/Logo';

const RESEND_COOLDOWN = 30;

const SignupPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [globalError, setGlobalError] = useState('');

  // "Check your email" screen state
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState('');

  const { signup, resendConfirmationEmail } = useAuth();
  const navigate = useNavigate();
  const cooldownTimerRef = useRef(null);

  useEffect(() => () => clearInterval(cooldownTimerRef.current), []);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    cooldownTimerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownTimerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const validate = () => {
    const errs = {};
    if (!formData.firstName.trim()) errs.firstName = 'First name is required.';
    if (!formData.lastName.trim()) errs.lastName = 'Last name is required.';
    if (!formData.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Enter a valid email address.';
    if (!formData.phone.trim()) errs.phone = 'Phone number is required.';
    if (!formData.password) errs.password = 'Password is required.';
    else if (formData.password.length < 8) errs.password = 'Password must be at least 8 characters.';
    if (!formData.confirmPassword) errs.confirmPassword = 'Please confirm your password.';
    else if (formData.password !== formData.confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }

    setIsLoading(true);
    const result = await signup(formData);
    setIsLoading(false);

    if (result.success) {
      if (result.needsEmailConfirmation) {
        setSentEmail(result.email || formData.email);
        setEmailSent(true);
        startCooldown();
      } else {
        navigate('/dashboard');
      }
    } else if (result.error === 'already_exists') {
      setFieldErrors({ email: 'An account with this email already exists.' });
      setGlobalError('already_exists');
    } else {
      setGlobalError(result.error);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResendStatus('sending');
    const result = await resendConfirmationEmail(sentEmail);
    setResendStatus(result.success ? 'sent' : 'error');
    if (result.success) startCooldown();
  };

  const inputCls = (field) =>
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent transition-all duration-200 ${
      fieldErrors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  const iconInputCls = (field) =>
    `pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent transition-all duration-200 ${
      fieldErrors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  // ── "Check your email" confirmation screen ──
  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Mail size={32} className="text-[#0038A8]" />
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your inbox</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              We sent a confirmation link to{' '}
              <span className="font-semibold text-gray-900">{sentEmail}</span>.
              Click the link in that email to activate your account.
            </p>
            <p className="text-gray-500 text-xs mt-2">Don't see it? Check your spam or junk folder.</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-md p-6 space-y-4">
            {resendStatus === 'sent' && (
              <p className="text-green-700 text-sm bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                Confirmation email resent successfully.
              </p>
            )}
            {resendStatus === 'error' && (
              <p className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                Something went wrong. Please try again.
              </p>
            )}
            <button
              onClick={handleResend}
              disabled={cooldown > 0 || resendStatus === 'sending'}
              className="w-full border border-[#0038A8] text-[#0038A8] py-2.5 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {resendStatus === 'sending'
                ? 'Sending...'
                : cooldown > 0
                ? `Resend email (${cooldown}s)`
                : 'Resend confirmation email'}
            </button>
            <p className="text-xs text-gray-500">
              Already confirmed?{' '}
              <Link to="/login" className="text-[#0038A8] hover:underline font-medium">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Signup form ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <Logo className="h-16 w-auto" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Create Your Account</h2>
          <p className="text-gray-600">Access your policies 24/7 and manage your coverage</p>
        </div>

        <div className="bg-white py-8 px-8 shadow-xl rounded-xl border border-gray-200">
          <form className="space-y-5" onSubmit={handleSubmit} noValidate>
            {globalError && globalError !== 'already_exists' && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {globalError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input name="firstName" type="text" value={formData.firstName} onChange={handleChange} className={inputCls('firstName')} placeholder="Maria" />
                {fieldErrors.firstName && <p className="mt-1 text-xs text-red-600">{fieldErrors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input name="lastName" type="text" value={formData.lastName} onChange={handleChange} className={inputCls('lastName')} placeholder="Santos" />
                {fieldErrors.lastName && <p className="mt-1 text-xs text-red-600">{fieldErrors.lastName}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="email" type="email" autoComplete="email" value={formData.email} onChange={handleChange} className={iconInputCls('email')} placeholder="you@example.com" />
              </div>
              {fieldErrors.email && (
                <p className="mt-1 text-xs text-red-600">
                  {fieldErrors.email}{' '}
                  {globalError === 'already_exists' && (
                    <Link to="/login" className="underline text-[#0038A8]">Log in instead.</Link>
                  )}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="phone" type="tel" value={formData.phone} onChange={handleChange} className={iconInputCls('phone')} placeholder="(562) 555-1234" />
              </div>
              {fieldErrors.phone && <p className="mt-1 text-xs text-red-600">{fieldErrors.phone}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="password" type="password" autoComplete="new-password" value={formData.password} onChange={handleChange} className={iconInputCls('password')} placeholder="At least 8 characters" />
              </div>
              {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-2.5 text-gray-400" size={18} />
                <input name="confirmPassword" type="password" autoComplete="new-password" value={formData.confirmPassword} onChange={handleChange} className={iconInputCls('confirmPassword')} placeholder="Repeat your password" />
              </div>
              {fieldErrors.confirmPassword && <p className="mt-1 text-xs text-red-600">{fieldErrors.confirmPassword}</p>}
            </div>

            <div className="flex items-start gap-2">
              <input type="checkbox" required className="mt-1 accent-[#DC143C]" />
              <div className="text-xs text-gray-600">I agree to the Terms of Service and Privacy Policy.</div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#DC143C] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B01030] transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <><div className="spinner"></div> Creating Account...</>
              ) : (
                <>Create Account <CheckCircle size={20} /></>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0038A8] hover:underline font-medium">Log In</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
